'use client';

import { useState, useRef } from 'react';
import { Upload, Sparkles, X, FileText, Image as ImageIcon, File, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface AttachedFile {
  file: File;
  id: string;
  name: string;
  type: string;
  size: number;
}

interface ArtifactGeneratorInputProps {
  placeholder: string;
  onGenerate: (description: string, files: AttachedFile[]) => Promise<void>;
  isGenerating?: boolean;
  className?: string;
}

// Format file size
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

// Get icon based on file type
function getFileIcon(mimeType: string) {
  if (mimeType.startsWith('image/')) return ImageIcon;
  if (mimeType.includes('pdf') || mimeType.includes('document') || mimeType.includes('text')) return FileText;
  return File;
}

export function ArtifactGeneratorInput({
  placeholder,
  onGenerate,
  isGenerating = false,
  className,
}: ArtifactGeneratorInputProps) {
  const [description, setDescription] = useState('');
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const newFiles: AttachedFile[] = files.map(file => ({
      file,
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: file.name,
      type: file.type,
      size: file.size,
    }));
    setAttachedFiles(prev => [...prev, ...newFiles]);
    event.target.value = '';
  };

  const removeFile = (fileId: string) => {
    setAttachedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const handleGenerate = async () => {
    if (!description.trim() && attachedFiles.length === 0) return;
    await onGenerate(description, attachedFiles);
    // Clear after successful generation
    setDescription('');
    setAttachedFiles([]);
  };

  const canGenerate = description.trim() || attachedFiles.length > 0;

  return (
    <div className={cn("bg-white border border-gray-200 rounded-[14px] p-6", className)}>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleFileChange}
        accept=".pdf,.doc,.docx,.txt,.md,.json,.csv,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.gif,.webp"
      />

      {/* Textarea */}
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder={placeholder}
        disabled={isGenerating}
        className="w-full h-[120px] p-4 border border-gray-300 rounded-[10px] bg-white text-base text-gray-900 placeholder:text-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary disabled:bg-gray-50 disabled:cursor-not-allowed"
      />

      {/* Attached Files Preview */}
      {attachedFiles.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {attachedFiles.map(file => {
            const Icon = getFileIcon(file.type);
            return (
              <div
                key={file.id}
                className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg text-sm group"
              >
                <Icon className="w-4 h-4 text-gray-500" />
                <span className="text-gray-700 truncate max-w-[150px]">{file.name}</span>
                <span className="text-gray-400 text-xs">{formatFileSize(file.size)}</span>
                <button
                  onClick={() => removeFile(file.id)}
                  disabled={isGenerating}
                  className="ml-1 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center justify-between mt-4">
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={isGenerating}
          className="h-10 px-4 flex items-center gap-2 border border-gray-300 rounded-[10px] hover:bg-gray-50 transition-colors text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Upload className="w-4 h-4" />
          <span className="text-base">Attach Files</span>
        </button>

        <button
          onClick={handleGenerate}
          disabled={!canGenerate || isGenerating}
          className={cn(
            "h-10 px-6 flex items-center gap-2 rounded-[10px] transition-colors text-white",
            canGenerate && !isGenerating
              ? "bg-brand-primary hover:bg-brand-primary/90"
              : "bg-gray-300 cursor-not-allowed"
          )}
        >
          {isGenerating ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Sparkles className="w-4 h-4" />
          )}
          <span className="text-base">
            {isGenerating ? 'Generating...' : 'Generate'}
          </span>
        </button>
      </div>
    </div>
  );
}

